import { FrontmatterMetadata, ValidationResult } from '../types.js';
import { FileSystemAdapter } from '../utils/file-system-adapter.js';
import { PathnameService } from './pathname-service.js';
import { load, dump, DEFAULT_SCHEMA, DumpOptions } from 'js-yaml';
import path from 'path';
import { logger } from '../utils/logger.js';
import { IntentService } from './intent-service.js';
import { FileService } from './file-service.js';
import { FileFormatValidator } from '../validation/file-format-validator.js';
import { MetadataFormatValidator } from '../validation/metadata-format-validator.js';
import { FileSystemValidator } from '../validation/file-system-validator.js';
import { ResolvedConfig } from '../utils/config.js';
import { PluginService } from './plugin-service.js';
import { parseDocSections, rebuildDocSections } from '../utils/doc-sections.js';
import { join } from 'path';

interface UpdateMetadata {
  author?: string;
  reason?: string;
  [key: string]: string | undefined;
}

interface UpdateOptions {
  id: string;
  content: string;
  metadata?: UpdateMetadata;
  time?: boolean;
}

interface XMLOptions {
  id: string;
  tagName: string;
  content: string;
  metadata?: Record<string, string>;
  time?: boolean;
}

export class IssueService {
  private readonly FRONTMATTER_REGEX = /^---\n([\s\S]*?\n)---\n([\s\S]*)$/;

  // YAML dump options for consistent frontmatter formatting
  private readonly YAML_DUMP_OPTIONS: DumpOptions = {
    lineWidth: -1,
    noRefs: true,
    schema: DEFAULT_SCHEMA,
    styles: {
      '!!null': 'lowercase',
      '!!bool': 'lowercase',
      '!!str': 'double'
    },
    quotingType: '"' as const,
    forceQuotes: true
  };

  private config: ResolvedConfig;
  private pathnameService: PathnameService;
  private fs: FileSystemAdapter;
  private intentService: IntentService;
  private fileService: FileService;
  private fileFormatValidator: FileFormatValidator;
  private metadataValidator: MetadataFormatValidator;
  private fileSystemValidator: FileSystemValidator;
  private pluginService?: PluginService;

  constructor(
    pathnameService: PathnameService,
    fs: FileSystemAdapter,
    intentService: IntentService,
    fileService: FileService,
    fileFormatValidator: FileFormatValidator,
    metadataValidator: MetadataFormatValidator,
    fileSystemValidator: FileSystemValidator,
    pluginService?: PluginService
  ) {
    this.pathnameService = pathnameService;
    this.fs = fs;
    this.intentService = intentService;
    this.fileService = fileService;
    this.fileFormatValidator = fileFormatValidator;
    this.metadataValidator = metadataValidator;
    this.fileSystemValidator = fileSystemValidator;
    this.pluginService = pluginService;
    this.config = pathnameService.getConfig();
  }

  fileMethods = {
    writeNewFile: async (metadata: FrontmatterMetadata, content: string = ''): Promise<string> => {
      // Clean and validate metadata
      const cleanedMeta = this.metadataValidator.cleanMetadata(metadata);
      const validation = this.validateMetadata(cleanedMeta);
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      // Create document sections
      const sections = {
        frontmatter: dump({
          ...cleanedMeta,
          created: new Date().toISOString()
        }, this.YAML_DUMP_OPTIONS),
        frontmatterObj: {
          ...cleanedMeta,
          created: new Date().toISOString()
        },
        content: content.trim(),
        xml: ''
      };

      // Build the document
      const fullContent = rebuildDocSections(sections);

      // Get path based on metadata
      const location = this.pathnameService.deriveFileLocation(cleanedMeta);
      const filePath = path.join(location.dirname, location.basename);

      // Ensure target directory exists
      await this.fileService.ensureDir(location.dirname);

      // Write file atomically
      await this.fileService.writeFile(filePath, fullContent);
      
      return filePath;
    },

    saveAndMoveFile: async (filePath: string, metadata: FrontmatterMetadata): Promise<string> => {
      // Let IntentService interpret the metadata
      const interpretedMeta = this.intentService.interpretMetadata(metadata);

      // Clean and validate the transition
      const currentMeta = await this.frontmatterMethods.getFrontmatterMetadata(filePath);
      
      const updatedMeta = this.intentService.cleanStateTransition(currentMeta || metadata, interpretedMeta);

      // Get new path based on interpreted metadata
      const location = this.pathnameService.deriveFileLocation(updatedMeta);
      const newPath = path.join(location.dirname, location.basename);

      // Read current content and update frontmatter
      const content = await this.fileService.readFile(filePath);
      const newContent = this.frontmatterMethods.updateFrontmatter(content, updatedMeta);

      // Normalize paths before comparison
      const normalizedNewPath = path.normalize(newPath);
      const normalizedFilePath = path.normalize(filePath);

      // If path is changing, use atomic move which will handle cleanup
      if (normalizedNewPath !== normalizedFilePath) {
        await this.fileService.atomicMove(filePath, newPath, newContent);
      } else {
        // Otherwise just update content in place
        await this.fileService.writeFile(filePath, newContent);
      }

      return newPath;
    },

    moveFileWithMetadata: async (sourcePath: string, targetPath: string, metadata: FrontmatterMetadata): Promise<void> => {
      // Get current metadata
      const currentMeta = await this.frontmatterMethods.getFrontmatterMetadata(sourcePath);
      if (!currentMeta) {
        throw new Error('No frontmatter found');
      }

      // Merge with new metadata (new values take precedence)
      const mergedMeta = {
        ...currentMeta,  // Start with all current metadata
        ...metadata,     // Override with new metadata
        created: currentMeta.created  // Always preserve created timestamp
      };

      // Let IntentService interpret the metadata
      const interpretedMeta = this.intentService.interpretMetadata(mergedMeta);
      
      // Clean and validate metadata before move
      const cleanedMeta = this.metadataValidator.cleanMetadata(interpretedMeta);
      const validation = this.validateMetadata(cleanedMeta);
      if (!validation.valid) {
        throw new Error(`Invalid metadata: ${validation.errors.join(', ')}`);
      }

      // Read current content and update frontmatter
      const content = await this.fileService.readFile(sourcePath);
      
      // Ensure we preserve the state in the frontmatter only if it exists
      const frontmatterToWrite = {
        ...cleanedMeta,
        ...('state' in metadata && { state: metadata.state })  // Only preserve state if it exists as a property
      };
      const newContent = this.frontmatterMethods.updateFrontmatter(content, frontmatterToWrite);
      
      // Get target path based on metadata
      const location = this.pathnameService.deriveFileLocation(cleanedMeta);
      const derivedTargetPath = path.join(location.dirname, location.basename);
      
      // Use derived path if no target path is provided
      const finalTargetPath = targetPath || derivedTargetPath;

      // Normalize paths for comparison
      const normalizedSource = path.normalize(sourcePath);
      const normalizedTarget = path.normalize(finalTargetPath);

      // Perform atomic move with backup
      await this.fileService.withBackup(normalizedSource, async () => {
        if (normalizedSource !== normalizedTarget) {
          await this.fileService.atomicMove(normalizedSource, normalizedTarget, newContent);
        } else {
          await this.fileService.writeFile(normalizedSource, newContent);
        }
      });
    },

    findFilesByState: async (state: string): Promise<string[]> => {
      // Let IntentService determine valid paths and state interpretation
      const stateInfo = this.intentService.interpretState(state);
      const paths = this.pathnameService.getPossiblePathsForState(state);
      
      const files = await this.fileService.findFiles((f) => f.endsWith('.md'), paths);
      const results: string[] = [];
      
      for (const file of files) {
        const meta = await this.frontmatterMethods.getFrontmatterMetadata(file);
        if (!meta) continue;
        
        // Special handling for inbox state
        if (state === 'inbox') {
          // For inbox, we only care about the file location and active status
          const normalizedFile = path.normalize(file);
          const normalizedInbox = path.normalize(this.config.inbox_dir);
          
          if (normalizedFile.startsWith(normalizedInbox) && meta.active === true) {
            results.push(file);
          }
          continue;
        }
        
        // For other states, let IntentService determine if file matches
        if (this.intentService.matchesState(meta, stateInfo)) {
          // Get canonical path based on metadata
          const location = this.pathnameService.deriveFileLocation(meta);
          const canonicalPath = path.join(location.dirname, location.basename);
          
          // Normalize paths for comparison
          const normalizedFile = path.normalize(file);
          const normalizedCanonical = path.normalize(canonicalPath);
          
          // Only add if the file is in the correct location
          if (normalizedFile === normalizedCanonical) {
            results.push(canonicalPath);
          }
        }
      }
      return results;
    },

    getMetadataFromPath: async (filePath: string): Promise<FrontmatterMetadata | null> => {
      try {
        const content = await this.fileService.readFile(filePath);
        const frontmatter = await this.frontmatterMethods.parseFrontmatter(content);
        if (!frontmatter) {
          return null;
        }
        return frontmatter as FrontmatterMetadata;
      } catch (error: any) {
        // Only catch "file not found" errors
        if (error.message === 'File not found') {
          return null;
        }
        throw error;
      }
    },

    findFileById: async (id: string): Promise<string | undefined> => {
      const searchDirs = this.pathnameService.getPossiblePathsForId(id);
      
      const matches: { path: string; meta: FrontmatterMetadata | null }[] = [];
      const errors: { path: string; error: Error }[] = [];
      
      for (const dir of searchDirs) {
        const filesInDir = await this.fileService.findFiles((f) => f.endsWith('.md'), [dir]);
        
        for (const file of filesInDir) {
          if (!this.pathnameService.matchesId(path.basename(file), id)) {
            continue;
          }

          try {
            const meta = await this.fileMethods.getMetadataFromPath(file);
            
            // Skip files with no metadata or no ID
            if (!meta || !meta.id) {
              continue;
            }

            // If metadata exists and has an ID, it must match exactly
            if (meta.id.toLowerCase() !== id.toLowerCase()) {
              // Throw immediately if IDs don't match
              throw new Error(`System integrity error: File ${file} has ID that does not match filename (${meta.id} vs ${id})`);
            }
            matches.push({ path: file, meta });
          } catch (error: any) {
            // Always rethrow system integrity errors
            if (error.message.includes('System integrity error')) {
              throw error;
            }
            errors.push({ path: file, error });
          }
        }
      }

      if (errors.length > 0) {
        const errorDetails = errors.map(e => `${e.path} (${e.error.message})`).join(', ');
        throw new Error(`System integrity error: Failed to parse metadata in files: ${errorDetails}`);
      }

      // Only consider matches where metadata ID matches exactly if present
      const validMatches = matches.filter(m => !m.meta?.id || m.meta.id.toLowerCase() === id.toLowerCase());

      if (validMatches.length > 1) {
        const foundFiles = validMatches.map(m => m.path).join(', ');
        throw new Error(`System integrity error: Multiple files found for ID ${id}: ${foundFiles}`);
      }

      return validMatches[0]?.path;
    },

    findFilesByType: async (type: string): Promise<string[]> => {
      const paths = this.pathnameService.getPossiblePathsForType(type);
      const files = await this.fileService.findFiles((f) => f.endsWith('.md'), paths);
      const results: string[] = [];
      for (const file of files) {
        const meta = await this.fileMethods.getMetadataFromPath(file);
        if (meta?.type === type) {
          results.push(file);
        }
      }
      return results;
    }
  };

  frontmatterMethods = {
    parseFrontmatter: async (content: string): Promise<Record<string, any> | null> => {
      // Use doc-sections to parse the content
      const sections = parseDocSections(content);
      return sections.frontmatterObj || null;
    },

    updateFrontmatter: (content: string, frontmatter: FrontmatterMetadata): string => {
      // Parse the document using doc-sections
      const sections = parseDocSections(content);
      
      // Update the frontmatter object
      sections.frontmatterObj = frontmatter;
      sections.frontmatter = dump(frontmatter, this.YAML_DUMP_OPTIONS);

      // Rebuild the document with the new frontmatter
      return rebuildDocSections(sections);
    },

    createFrontmatter: async (metadata: FrontmatterMetadata): Promise<string> => {
      const frontmatter = {
        ...metadata,
        created: new Date().toISOString()
      };

      // Create a new document with only frontmatter
      const sections = {
        frontmatter: dump(frontmatter, this.YAML_DUMP_OPTIONS),
        frontmatterObj: frontmatter,
        content: '',
        xml: ''
      };

      // Build the document
      return rebuildDocSections(sections);
    },

    getFrontmatterMetadata: async (filePath: string): Promise<FrontmatterMetadata | null> => {
      try {
        const content = await this.fileService.readFile(filePath);
        const sections = parseDocSections(content);
        // Return null if there is no frontmatter or if frontmatterObj is empty
        if (!sections.frontmatter || Object.keys(sections.frontmatterObj).length === 0) {
          return null;
        }
        return sections.frontmatterObj as FrontmatterMetadata;
      } catch (error: any) {
        if (error.message === 'File not found') {
          return null;
        }
        throw error;
      }
    },

    isValidFrontmatterMetadata: (meta: any): meta is FrontmatterMetadata => {
      return (
        typeof meta === 'object' &&
        meta !== null &&
        typeof meta.id === 'string' &&
        typeof meta.type === 'string' &&
        typeof meta.active === 'boolean'
      );
    },

    validateMetadata: (metadata: FrontmatterMetadata): ValidationResult => {
      // First validate format
      const formatValidation = this.metadataValidator.validateFormat(metadata);
      if (!formatValidation.valid) {
        return formatValidation;
      }

      // Then apply plugin validation chain if plugins exist
      if (this.pluginService) {
        const pluginValidation = this.xmlMethods.applyPluginValidation(metadata);
        if (!pluginValidation.valid) {
          return pluginValidation;
        }

        // If plugin validation passed but had warnings, merge them with config validation
        const configValidation = this.intentService.validateMetadataAgainstConfig(metadata);
        return {
          valid: configValidation.valid,
          errors: configValidation.errors,
          warnings: [...pluginValidation.warnings, ...configValidation.warnings]
        };
      }

      // Finally validate against config/business rules
      return this.intentService.validateMetadataAgainstConfig(metadata);
    },

    validateStateTransition: (current: FrontmatterMetadata, next: FrontmatterMetadata): ValidationResult => {
      return this.intentService.validateStateTransition(
        { pathname: '', metadata: current },
        { pathname: '', metadata: next }
      );
    },

    markAsDone: async (id: string, reason: string): Promise<string> => {
      const filePath = await this.fileMethods.findFileById(id);
      if (!filePath) {
        throw new Error(`File not found for id: ${id}`);
      }

      // Update metadata with done state
      const updates: Partial<FrontmatterMetadata> = {
        active: false,
        state: reason || 'done',
        inactive_timestamp: new Date().toISOString()
      };

      // Apply the state transition
      await this.frontmatterMethods.transitionActiveState(filePath, updates);

      return filePath;
    },

    setFrontmatterMetadata: async (filePath: string, updates: Partial<FrontmatterMetadata>): Promise<string> => {
      // Get current metadata
      const currentMeta = await this.frontmatterMethods.getFrontmatterMetadata(filePath);
      if (!currentMeta) {
        throw new Error('No frontmatter found');
      }

      // Merge updates with current metadata
      const updatedMeta = {
        ...currentMeta,
        ...updates
      };

      // Let IntentService interpret the metadata
      const interpretedMeta = this.intentService.interpretMetadata(updatedMeta);

      // Get new path based on interpreted metadata
      const location = this.pathnameService.deriveFileLocation(interpretedMeta);
      const newPath = path.join(location.dirname, location.basename);

      // Read current content and update frontmatter
      const content = await this.fileService.readFile(filePath);
      const newContent = this.frontmatterMethods.updateFrontmatter(content, interpretedMeta);

      // Normalize paths before comparison
      const normalizedNewPath = path.normalize(newPath);
      const normalizedFilePath = path.normalize(filePath);

      // If path is changing, use atomic move which will handle cleanup
      if (normalizedNewPath !== normalizedFilePath) {
        await this.fileService.atomicMove(filePath, newPath, newContent);
      } else {
        // Otherwise just update content in place
        await this.fileService.writeFile(filePath, newContent);
      }

      return newPath;
    },

    transitionActiveState: async (filePath: string, next: Partial<FrontmatterMetadata>): Promise<void> => {
      const currentMeta = await this.frontmatterMethods.getFrontmatterMetadata(filePath);
      if (!currentMeta) {
        throw new Error('Could not get current metadata');
      }

      // Let IntentService interpret the metadata
      const interpretedMeta = this.intentService.interpretMetadata(next);

      // Clean and validate the transition
      const updatedMeta = this.intentService.cleanStateTransition(currentMeta, interpretedMeta);
      const validation = this.frontmatterMethods.validateStateTransition(currentMeta, updatedMeta);
      if (!validation.valid) {
        throw new Error(`Invalid state transition: ${validation.errors.join(', ')}`);
      }

      // Get new path and move file
      const location = this.pathnameService.deriveFileLocation(updatedMeta);
      const newPath = path.join(location.dirname, location.basename);

      // Read current content and update frontmatter
      const content = await this.fileService.readFile(filePath);
      const newContent = this.frontmatterMethods.updateFrontmatter(content, updatedMeta);

      // Write to new location atomically
      await this.fileService.atomicMove(filePath, newPath, newContent);
    }
  };

  xmlMethods = {
    formatXMLAttributes: (metadata: Record<string, string> = {}, timestamp: string | false = false): string => {
      const attrs: string[] = [];
      
      // Add timestamp first if provided
      if (timestamp !== false) {
        attrs.push(`time="${timestamp}"`);
      }
      
      // Add all other metadata attributes
      for (const [key, value] of Object.entries(metadata)) {
        if (value !== undefined) {
          // Escape quotes in attribute values
          const escapedValue = value.replace(/"/g, '&quot;');
          attrs.push(`${key}="${escapedValue}"`);
        }
      }
      
      return attrs.length > 0 ? ' ' + attrs.join(' ') : '';
    },

    createXMLBlock: (tagName: string, content: string, metadata?: Record<string, string>, includeTime: boolean = false): string => {
      const timestamp = includeTime ? new Date().toISOString() : false;
      const attributes = this.xmlMethods.formatXMLAttributes(metadata, timestamp);
      
      // Ensure content has proper line endings and no extra whitespace
      const normalizedContent = content.trim();
      
      // Return XML block with proper spacing - single newline within block
      return `<${tagName}${attributes}>\n${normalizedContent}\n</${tagName}>`;
    },

    prependXML: async (options: XMLOptions): Promise<void> => {
      const { id, tagName, content, metadata, time = false } = options;

      // Find the file using findFileById
      const filePath = await this.fileMethods.findFileById(id);
      if (!filePath) {
        throw new Error(`File not found for id: ${id}`);
      }

      // Read current content
      const currentContent = await this.fileService.readFile(filePath);

      // Parse using doc-sections
      const sections = parseDocSections(currentContent);

      // Create new XML block
      const xmlBlock = this.xmlMethods.createXMLBlock(tagName, content, metadata, time);

      // Prepend the new block to existing XML content with proper spacing
      sections.xml = sections.xml
        ? `${xmlBlock}\n\n${sections.xml.trim()}`
        : xmlBlock;

      // Rebuild the document
      const newContent = rebuildDocSections(sections);

      // Write updated content atomically
      await this.fileService.writeFile(filePath, newContent);
    },

    appendXML: async (options: XMLOptions): Promise<void> => {
      const { id, tagName, content, metadata, time = false } = options;

      // Find the file using findFileById
      const filePath = await this.fileMethods.findFileById(id);
      if (!filePath) {
        throw new Error(`File not found for id: ${id}`);
      }

      // Read current content
      const currentContent = await this.fileService.readFile(filePath);

      // Parse using doc-sections
      const sections = parseDocSections(currentContent);

      // Create new XML block
      const xmlBlock = this.xmlMethods.createXMLBlock(tagName, content, metadata, time);

      // Append the new block to existing XML content with proper spacing
      sections.xml = sections.xml
        ? `${sections.xml.trim()}\n\n${xmlBlock}`
        : xmlBlock;

      // Rebuild the document
      const newContent = rebuildDocSections(sections);

      // Write updated content atomically
      await this.fileService.writeFile(filePath, newContent);
    },

    prependUpdate: async (options: UpdateOptions): Promise<void> => {
      const { metadata, ...rest } = options;
      // Convert UpdateMetadata to Record<string, string> by filtering out undefined values
      const cleanedMetadata = metadata ? 
        Object.fromEntries(
          Object.entries(metadata)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, value as string])  // Assert non-undefined values are strings
        ) as Record<string, string> : undefined;

      await this.xmlMethods.prependXML({
        ...rest,
        tagName: 'Update',
        time: options.time ?? true, // Default to true for Update blocks
        metadata: cleanedMetadata
      });
    },

    appendUpdate: async (options: UpdateOptions): Promise<void> => {
      const { metadata, ...rest } = options;
      // Convert UpdateMetadata to Record<string, string> by filtering out undefined values
      const cleanedMetadata = metadata ? 
        Object.fromEntries(
          Object.entries(metadata)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, value as string])  // Assert non-undefined values are strings
        ) as Record<string, string> : undefined;

      await this.xmlMethods.appendXML({
        ...rest,
        tagName: 'Update',
        time: options.time ?? true, // Default to true for Update blocks
        metadata: cleanedMetadata
      });
    },

    applyPluginValidation: (metadata: FrontmatterMetadata): ValidationResult => {
      const result: ValidationResult = { 
        valid: true, 
        errors: [], 
        warnings: []
      };

      // Get plugins in priority order (already sorted by PluginService)
      const plugins = this.pluginService!.getPlugins();

      for (const plugin of plugins) {
        try {
          if (plugin.validateMetadata) {
            const pluginResult = plugin.validateMetadata(metadata);
            
            // Merge warnings
            if (pluginResult.warnings) {
              result.warnings.push(...pluginResult.warnings);
            }

            // If plugin validation fails, merge errors and mark as invalid
            if (!pluginResult.valid) {
              result.valid = false;
              if (pluginResult.errors) {
                result.errors.push(...pluginResult.errors);
              }
            }
          }
        } catch (error: any) {
          // Log error but continue with other plugins
          logger.error(`Plugin ${plugin.name} validation failed:`, error);
          result.warnings.push(`Plugin ${plugin.name} validation error: ${error.message}`);
        }
      }

      return result;
    }
  };

  // Public methods for validation
  validateMetadata = this.frontmatterMethods.validateMetadata;
  validateStateTransition = this.frontmatterMethods.validateStateTransition;

  getIssueDirectories(): string[] {
    const dirs = [];
    const paths = this.pathnameService.getActivityDirectories();
    const issueTypes = this.pathnameService.getConfig().types_list;
    for (const type of issueTypes) {
      for (const path of paths) {
        dirs.push(join(path, type));
      }
    }
    return dirs;
  }
} 